import React, { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { 
    MapPin, 
    Music, 
    Users, 
    Calendar,
    Building2,
    ChevronDown,
    ChevronRight,
    Menu,
    X,
    Layers,
    GraduationCap,
    Search,
    Settings,
    LogOut,
    Download,
    FileText,
    Shield,
    Wrench,
    Power,
    Key,
    BarChart3
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const menuItems = [
    {
        id: 'uebersicht',
        label: 'Übersicht',
        icon: Search,
        path: '/admin',
        exact: true
    },
    {
        id: 'angebote',
        label: 'Angebote',
        icon: Calendar,
        path: '/admin/angebote'
    },
    {
        id: 'stammdaten',
        label: 'Stammdaten',
        icon: Layers,
        children: [
            { id: 'instrumente', label: 'Instrumente', path: '/admin/instrumente', icon: Music },
            { id: 'lehrpersonen', label: 'Lehrpersonen', path: '/admin/lehrpersonen', icon: GraduationCap },
            { id: 'standorte', label: 'Standorte', path: '/admin/standorte', icon: MapPin },
        ]
    },
    {
        id: 'export',
        label: 'Export & Druck',
        icon: Download,
        path: '/admin/export'
    },
    {
        id: 'statistik',
        label: 'Statistik',
        icon: BarChart3,
        path: '/admin/statistik'
    },
    {
        id: 'einstellungen',
        label: 'Einstellungen',
        icon: Settings,
        children: [
            { id: 'allgemein', label: 'Allgemein', path: '/admin/einstellungen', icon: Wrench, exact: true },
            { id: 'texte', label: 'Textverwaltung', path: '/admin/einstellungen/texte', icon: FileText },
            { id: 'benutzer', label: 'Benutzerverwaltung', path: '/admin/einstellungen/benutzer', icon: Users },
            { id: 'rollen', label: 'Rollenverwaltung', path: '/admin/einstellungen/rollen', icon: Shield },
            { id: 'system', label: 'System', path: '/admin/einstellungen/system', icon: Power },
        ]
    }
];

// Filter menu items based on user role
const getFilteredMenuItems = (userRole) => {
    const canEditSettings = userRole === 'super_admin';
    const canManageUsers = ['super_admin', 'admin'].includes(userRole);
    const isViewerOnly = userRole === 'betrachter';
    
    return menuItems.map(item => {
        // Filter einstellungen children based on permissions
        if (item.id === 'einstellungen') {
            const filteredChildren = item.children.filter(child => {
                // System settings only for super_admin
                if (child.id === 'system' && !canEditSettings) return false;
                // Allgemein settings only for super_admin
                if (child.id === 'allgemein' && !canEditSettings) return false;
                // Textverwaltung only for super_admin
                if (child.id === 'texte' && !canEditSettings) return false;
                // Rollenverwaltung only for super_admin
                if (child.id === 'rollen' && !canEditSettings) return false;
                // Benutzerverwaltung for admin+
                if (child.id === 'benutzer' && !canManageUsers) return false;
                return true;
            });
            
            // Hide entire einstellungen section if no children
            if (filteredChildren.length === 0) return null;
            
            return { ...item, children: filteredChildren };
        }
        
        return item;
    }).filter(Boolean);
};

export function AdminSidebar() {
    const [openSections, setOpenSections] = useState(['stammdaten']);
    const [mobileOpen, setMobileOpen] = useState(false);
    const location = useLocation();
    
    // Get user from localStorage - initialize with stored value
    const getStoredUserRole = () => {
        try {
            const userData = localStorage.getItem('user');
            if (userData) {
                const user = JSON.parse(userData);
                return user.role || 'betrachter';
            }
        } catch (e) {
            console.error('Error parsing user data:', e);
        }
        return 'super_admin'; // Default to super_admin to show all menu items until loaded
    };
    
    const [userRole, setUserRole] = useState(getStoredUserRole);
    
    React.useEffect(() => {
        // Update role when localStorage changes
        const handleStorageChange = () => {
            setUserRole(getStoredUserRole());
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);
    
    const filteredMenuItems = getFilteredMenuItems(userRole);

    const toggleSection = (sectionId) => {
        setOpenSections(prev => 
            prev.includes(sectionId) 
                ? prev.filter(id => id !== sectionId)
                : [...prev, sectionId]
        );
    };

    const isActive = (path, exact = false) => {
        if (exact) {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    };

    const NavItem = ({ item }) => {
        const Icon = item.icon;
        const hasChildren = item.children && item.children.length > 0;
        const isOpen = openSections.includes(item.id);
        const active = item.path ? isActive(item.path, item.exact) : false;

        if (hasChildren) {
            return (
                <div className="mb-1">
                    <button
                        onClick={() => toggleSection(item.id)}
                        className={cn(
                            "admin-nav-item w-full justify-between",
                            isOpen && "bg-[hsl(var(--sidebar-muted))]"
                        )}
                        aria-expanded={isOpen}
                    >
                        <span className="flex items-center gap-3">
                            <Icon className="admin-nav-item-icon" />
                            <span>{item.label}</span>
                        </span>
                        {isOpen ? (
                            <ChevronDown className="w-4 h-4" />
                        ) : (
                            <ChevronRight className="w-4 h-4" />
                        )}
                    </button>
                    {isOpen && (
                        <div className="admin-nav-submenu animate-fade-in">
                            {item.children.map(child => (
                                <NavLink
                                    key={child.id}
                                    to={child.path}
                                    end={child.exact}
                                    onClick={() => setMobileOpen(false)}
                                    className={({ isActive }) => cn(
                                        "admin-nav-submenu-item",
                                        isActive && "active"
                                    )}
                                >
                                    {child.icon && <child.icon className="w-4 h-4" />}
                                    <span>{child.label}</span>
                                </NavLink>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <NavLink
                to={item.path}
                onClick={() => setMobileOpen(false)}
                className={cn(
                    "admin-nav-item",
                    active && "active"
                )}
            >
                <Icon className="admin-nav-item-icon" />
                <span>{item.label}</span>
            </NavLink>
        );
    };

    return (
        <>
            {/* Mobile menu button */}
            <Button
                variant="ghost"
                size="icon"
                className="fixed top-4 left-4 z-50 lg:hidden"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
            >
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>

            {/* Mobile overlay */}
            {mobileOpen && (
                <div 
                    className="mobile-menu-overlay lg:hidden"
                    onClick={() => setMobileOpen(false)}
                    aria-hidden="true"
                />
            )}

            {/* Sidebar */}
            <aside 
                className={cn(
                    "admin-sidebar",
                    mobileOpen && "open"
                )}
                role="navigation"
                aria-label="Hauptnavigation"
            >
                <div className="admin-sidebar-header justify-center">
                    <img src="/logo.svg" alt="Logo" className="h-12 object-contain" />
                </div>

                <nav className="admin-sidebar-nav">
                    <div className="admin-sidebar-section">
                        <p className="admin-sidebar-section-title">Navigation</p>
                        {filteredMenuItems.map(item => (
                            <NavItem key={item.id} item={item} />
                        ))}
                    </div>
                </nav>

                <div className="mt-auto px-4 py-4 border-t border-[hsl(var(--sidebar-border))]">
                    <UserInfo />
                </div>
            </aside>
        </>
    );
}

function UserInfo() {
    const navigate = useNavigate();
    const [user, setUser] = useState(() => {
        const stored = localStorage.getItem('auth_user');
        return stored ? JSON.parse(stored) : null;
    });
    const [showPasswordDialog, setShowPasswordDialog] = useState(false);
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    
    const handleLogout = () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        navigate('/login');
    };
    
    const handleChangePassword = async () => {
        if (!passwordData.current_password || !passwordData.new_password) {
            toast.error('Bitte alle Felder ausfüllen');
            return;
        }
        
        if (passwordData.new_password.length < 6) {
            toast.error('Neues Passwort muss mindestens 6 Zeichen haben');
            return;
        }
        
        if (passwordData.new_password !== passwordData.confirm_password) {
            toast.error('Passwörter stimmen nicht überein');
            return;
        }
        
        setIsChangingPassword(true);
        try {
            const token = localStorage.getItem('auth_token');
            await axios.put(
                `${API_URL}/api/auth/change-password`,
                {
                    current_password: passwordData.current_password,
                    new_password: passwordData.new_password
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Passwort erfolgreich geändert');
            setShowPasswordDialog(false);
            setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
        } catch (error) {
            const msg = error.response?.data?.detail || 'Fehler beim Ändern des Passworts';
            toast.error(msg);
        } finally {
            setIsChangingPassword(false);
        }
    };
    
    if (!user) {
        return (
            <NavLink 
                to="/login"
                className="flex items-center gap-2 text-sm text-[hsl(var(--sidebar-foreground)/0.6)] hover:text-[hsl(var(--sidebar-foreground))]"
            >
                <LogOut className="w-4 h-4" />
                Anmelden
            </NavLink>
        );
    }
    
    return (
        <>
            <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[hsl(var(--sidebar-accent))] flex items-center justify-center text-xs font-semibold text-white">
                            {user.vorname?.[0] || user.name?.[0] || user.email?.[0]?.toUpperCase() || 'U'}
                            {user.nachname?.[0] || ''}
                        </div>
                        <div className="text-sm">
                            <p className="font-medium text-[hsl(var(--sidebar-foreground))]">
                                {user.vorname && user.nachname 
                                    ? `${user.vorname} ${user.nachname}` 
                                    : user.name || user.email?.split('@')[0] || 'Benutzer'}
                            </p>
                            <p className="text-xs text-[hsl(var(--sidebar-foreground)/0.5)]">
                                {user.role === 'super_admin' ? 'Super Admin' : 
                                 user.role === 'admin' ? 'Admin' : 
                                 user.role === 'verwaltung' ? 'Verwaltung' :
                                 user.role === 'betrachter' ? 'Betrachter' :
                                 user.role === 'koordinator' ? 'Koordinator' : 'Lehrperson'}
                            </p>
                        </div>
                    </div>
                    <button 
                        onClick={handleLogout}
                        className="p-2 rounded-lg hover:bg-[hsl(var(--sidebar-accent)/0.1)] text-[hsl(var(--sidebar-foreground)/0.5)] hover:text-[hsl(var(--sidebar-foreground))]"
                        title="Abmelden"
                    >
                        <LogOut className="w-4 h-4" />
                    </button>
                </div>
                
                {/* Change Password Button */}
                <button
                    onClick={() => setShowPasswordDialog(true)}
                    className="flex items-center gap-2 text-xs text-[hsl(var(--sidebar-foreground)/0.5)] hover:text-[hsl(var(--sidebar-foreground))] transition-colors"
                >
                    <Key className="w-3 h-3" />
                    Passwort ändern
                </button>
            </div>
            
            {/* Password Change Dialog */}
            <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Passwort ändern</DialogTitle>
                        <DialogDescription>
                            Gib dein aktuelles und neues Passwort ein.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="current_password">Aktuelles Passwort</Label>
                            <Input
                                id="current_password"
                                type="password"
                                value={passwordData.current_password}
                                onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                                placeholder="••••••••"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="new_password">Neues Passwort</Label>
                            <Input
                                id="new_password"
                                type="password"
                                value={passwordData.new_password}
                                onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                                placeholder="Mindestens 6 Zeichen"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="confirm_password">Neues Passwort bestätigen</Label>
                            <Input
                                id="confirm_password"
                                type="password"
                                value={passwordData.confirm_password}
                                onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                                placeholder="••••••••"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setShowPasswordDialog(false)}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleChangePassword} disabled={isChangingPassword}>
                            {isChangingPassword ? 'Wird geändert...' : 'Passwort ändern'}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}

export function AdminHeader({ title, children }) {
    return (
        <header className="admin-header">
            <div className="flex items-center gap-4 lg:ml-0 ml-12">
                <h1 className="heading-2">{title}</h1>
            </div>
            <div className="flex items-center gap-3">
                {children}
            </div>
        </header>
    );
}

export function AdminLayout({ children }) {
    return (
        <div className="admin-layout">
            <AdminSidebar />
            <main className="admin-main">
                {children}
            </main>
        </div>
    );
}
